import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import { getDramaById, getRelatedDramas } from '@/utils/drama';

const DEMO_DURATION = 60; // 演示时长（秒）

export default function Play() {
  const { id, episode } = useParams();
  const navigate = useNavigate();
  const drama = getDramaById(id);
  const epNumber = Number(episode);

  const [loading, setLoading] = useState(true);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);

  const valid = !!drama && Number.isFinite(epNumber) && epNumber >= 1 && epNumber <= (drama?.latestEpisode || 0);

  useEffect(() => {
    setLoading(true);
    setPlaying(false);
    setProgress(0);
    const timer = setTimeout(() => setLoading(false), 700);
    return () => clearTimeout(timer);
  }, [id, episode]);

  useEffect(() => {
    if (!playing) return;
    const timer = setInterval(() => {
      setProgress((p) => {
        if (p >= 100) {
          setPlaying(false);
          return 100;
        }
        return p + 100 / DEMO_DURATION;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [playing]);

  if (!drama || !valid) {
    return (
      <SiteLayout>
        <title>播放演示 - 善良的小姨子在线</title>
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-center px-4 py-24 text-center md:px-6">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
            <i className="ri-error-warning-line text-3xl"></i>
          </span>
          <h1 className="mt-4 font-heading text-xl font-bold text-foreground-950">
            无法播放该剧集
          </h1>
          <p className="mt-2 max-w-md text-sm text-foreground-600">
            剧集不存在，或该集数尚未更新。请返回详情页选择其他可播放的集数。
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            {drama && (
              <Link
                to={`/detail/${drama.id}`}
                className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i>
                返回剧集详情
              </Link>
            )}
            <Link
              to="/category"
              className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 cursor-pointer"
            >
              <i className="ri-compass-3-line"></i>
              浏览其他剧集
            </Link>
          </div>
        </div>
      </SiteLayout>
    );
  }

  const related = getRelatedDramas(drama, 4);

  return (
    <SiteLayout>
      <title>{`${drama.title} 第${epNumber}集 - 在线观看演示 - 善良的小姨子在线`}</title>
      <meta name="description" content={`${drama.title}第${epNumber}集在线观看演示页面，${drama.synopsis.slice(0, 36)}...`} />
      <meta name="keywords" content={`${drama.title}第${epNumber}集,${drama.title}在线观看,善良的小姨子在线`} />

      <div className="mx-auto max-w-6xl px-4 pt-6 md:px-6">
        <nav className="flex flex-wrap items-center gap-1 text-sm text-foreground-600" aria-label="面包屑">
          <Link to="/" className="transition-colors hover:text-primary-600">首页</Link>
          <i className="ri-arrow-right-s-line text-foreground-300"></i>
          <Link to={`/detail/${drama.id}`} className="transition-colors hover:text-primary-600">{drama.title}</Link>
          <i className="ri-arrow-right-s-line text-foreground-300"></i>
          <span className="text-foreground-900">第 {epNumber} 集</span>
        </nav>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
          <h1 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
            {drama.title} · 第 {epNumber} 集
          </h1>
          <span className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900">
            演示模式 · 暂无真实视频资源
          </span>
        </div>
      </div>

      <div className="mx-auto mt-4 max-w-6xl px-4 md:px-6">
        <div className="grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <div className="relative w-full overflow-hidden rounded-lg bg-foreground-950">
              <div className="relative aspect-video w-full">
                <img
                  src={drama.backdrop || drama.poster}
                  alt={`${drama.title} 第${epNumber}集 播放画面`}
                  title={`${drama.title} 播放演示`}
                  className={`h-full w-full object-cover object-top transition-all duration-500 ${
                    playing ? 'opacity-80' : 'opacity-40'
                  }`}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/90 to-foreground-950/20"></div>

                {loading && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-foreground-950/85">
                    <i className="ri-loader-4-line animate-spin text-3xl text-primary-400"></i>
                    <p className="text-sm text-background-200">正在加载播放画面…</p>
                  </div>
                )}

                {!loading && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <button
                      type="button"
                      onClick={() => setPlaying((p) => !p)}
                      aria-label={playing ? '暂停' : '播放'}
                      className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-500 text-background-50 transition-transform hover:scale-105 cursor-pointer md:h-20 md:w-20"
                    >
                      <i className={playing ? 'ri-pause-fill text-3xl' : 'ri-play-fill text-3xl'}></i>
                    </button>
                  </div>
                )}

                {!loading && (
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <div className="h-1.5 w-full overflow-hidden rounded-full bg-background-50/30">
                      <div
                        className="h-full rounded-full bg-primary-500 transition-all duration-1000"
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                    <div className="mt-3 flex items-center justify-between gap-3 text-xs text-background-200">
                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          onClick={() => setPlaying((p) => !p)}
                          className="flex h-8 w-8 items-center justify-center rounded-full bg-background-50/20 text-background-50 hover:bg-background-50/40 cursor-pointer"
                          aria-label={playing ? '暂停' : '播放'}
                        >
                          <i className={playing ? 'ri-pause-line' : 'ri-play-line'}></i>
                        </button>
                        <span>第 {epNumber} 集</span>
                      </div>
                      <span>{Math.floor(progress)}% · 演示进度</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-3">
              <button
                type="button"
                disabled={epNumber <= 1}
                onClick={() => navigate(`/play/${drama.id}/${epNumber - 1}`)}
                className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm text-foreground-700 transition-colors hover:border-primary-300 disabled:cursor-not-allowed disabled:opacity-40 cursor-pointer"
              >
                <i className="ri-skip-back-line"></i>
                上一集
              </button>
              <button
                type="button"
                disabled={epNumber >= drama.latestEpisode}
                onClick={() => navigate(`/play/${drama.id}/${epNumber + 1}`)}
                className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm text-foreground-700 transition-colors hover:border-primary-300 disabled:cursor-not-allowed disabled:opacity-40 cursor-pointer"
              >
                下一集
                <i className="ri-skip-forward-line"></i>
              </button>
              <Link
                to={`/detail/${drama.id}`}
                className="flex items-center gap-2 whitespace-nowrap rounded-md bg-secondary-500 px-4 py-2 text-sm text-background-50 transition-colors hover:bg-secondary-600 cursor-pointer"
              >
                <i className="ri-list-check-2"></i>
                剧集列表
              </Link>
            </div>

            <section className="mt-6 rounded-lg border border-background-200 bg-background-50 p-4 md:p-6">
              <h2 className="flex items-center gap-2 font-heading text-lg font-semibold text-foreground-950">
                <i className="ri-file-text-line text-primary-500"></i>
                剧情简介
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-700">
                {drama.synopsis}
              </p>
            </section>
          </div>

          <aside className="lg:col-span-1">
            <section className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-6">
              <h2 className="flex items-center gap-2 font-heading text-lg font-semibold text-foreground-950">
                <i className="ri-play-list-2-line text-primary-500"></i>
                选集
              </h2>
              <div className="mt-4 grid max-h-[420px] grid-cols-5 gap-2 overflow-y-auto pr-1">
                {Array.from({ length: drama.latestEpisode }, (_, i) => i + 1).map((ep) => (
                  <Link
                    key={ep}
                    to={`/play/${drama.id}/${ep}`}
                    className={`flex h-9 items-center justify-center rounded-md border text-sm transition-colors cursor-pointer ${
                      ep === epNumber
                        ? 'border-primary-500 bg-primary-500 text-background-50'
                        : 'border-background-300 text-foreground-700 hover:border-primary-300'
                    }`}
                  >
                    {ep}
                  </Link>
                ))}
              </div>
            </section>

            <section className="mt-4 rounded-lg border border-background-200 bg-background-50 p-4 md:p-6">
              <h2 className="flex items-center gap-2 font-heading text-lg font-semibold text-foreground-950">
                <i className="ri-magic-line text-primary-500"></i>
                相关推荐
              </h2>
              <div className="mt-4 space-y-3">
                {related.map((d) => (
                  <Link
                    key={d.id}
                    to={`/detail/${d.id}`}
                    className="group flex gap-3 rounded-md p-2 transition-colors hover:bg-primary-50/50 cursor-pointer"
                  >
                    <div className="h-20 w-14 shrink-0 overflow-hidden rounded-md bg-background-200">
                      <img
                        src={d.poster}
                        alt={`${d.title} 海报`}
                        title={`${d.title} 相关推荐`}
                        className="h-full w-full object-cover object-top"
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="truncate text-sm font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors">
                        {d.title}
                      </h3>
                      <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-foreground-600">
                        {d.synopsis}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          </aside>
        </div>
      </div>
    </SiteLayout>
  );
}